<template>
  <div>
    <n-input
      v-model="internalValue"
      @change="$emit('inputChange', internalValue)"
      v-bind="inputProps"
      @keydown.enter.prevent="addValue"
      @keydown="onKeydown"
      @blur="addValue"
    >
      <n-chip v-for="val in value" :key="val" :name="val" :closable="!disabled" @close="removeValue(val)" />
    </n-input>
  </div>
</template>

<script lang="ts">
import { Component, Prop, Vue } from 'vue-property-decorator'
import NInput from './NInput.vue'
import NChip from './NChip.vue'
import type { ValidationResult } from 'vee-validate/dist/types/types'

type InputProps = Pick<
  NInput,
  | 'label'
  | 'value'
  | 'disabled'
  | 'placeholder'
  | 'error'
  | 'active'
  | 'variableHeight'
  | 'iconRight'
  | 'iconLeft'
  | 'info'
  | 'optional'
>

@Component({
  name: 'n-multiple-input',
  components: {
    NInput,
    NChip
  },
  model: { prop: 'value', event: 'change' }
})
export default class NMultipleInput extends Vue {
  @Prop({ default: undefined }) label?: string
  @Prop({ default: '' }) placeholder!: HTMLInputElement['placeholder']
  @Prop({ default: '' }) info!: string
  @Prop({ default: false, type: Boolean }) optional?: boolean

  @Prop({ default: undefined, type: Boolean }) disabled?: HTMLInputElement['disabled']
  @Prop({ default: undefined }) error?: string

  @Prop({ default: () => [] }) value!: string[]

  @Prop({
    default: () => async () => ({
      valid: true,
      errors: []
    })
  })
  validateInput!: (input: string) => Promise<ValidationResult>
  @Prop({ default: () => [','] }) delimiters!: string[]
  @Prop({ default: undefined }) iconRight?: string
  @Prop({ default: undefined }) iconLeft?: string

  internalValue = ''
  private internalErrors: ValidationResult['errors'] = []

  private doReveal = false

  private get inputProps(): InputProps {
    return {
      label: this.label,
      placeholder: this.placeholder,
      info: this.info,
      disabled: this.disabled,
      error: this.error || this.internalErrors[0],
      active: !!this.value.length,
      variableHeight: !!this.value.length,
      iconRight: this.iconRight,
      iconLeft: this.iconLeft,
      optional: this.optional
    }
  }

  onKeydown(event: KeyboardEvent) {
    if (event.key === 'Backspace' && !this.internalValue) {
      this.removeValue(this.value[this.value.length - 1])
    } else if (this.delimiters.includes(event.key)) {
      event.preventDefault()
      event.stopPropagation()
      this.addValue()
    } else if (event.key !== 'Enter') {
      this.internalErrors = []
    }
  }

  async addValue() {
    if (!this.internalValue) {
      return
    }

    const validationResult = await this.validateInput(this.internalValue)
    this.internalErrors = validationResult.errors

    if (validationResult.valid) {
      if (!this.value.includes(this.internalValue)) {
        this.$emit('addValue', this.internalValue)
        this.$emit('change', [...this.value, this.internalValue])
      }
      this.internalValue = ''
    }
  }

  removeValue(value: string) {
    this.$emit(
      'change',
      this.value.filter(v => v !== value)
    )
    this.$emit('removeValue', value)
  }
}
</script>
